import { clamp } from './audio-player-utils';
import type { ProgressClusterProps } from './types';

const DEFAULT_TOTAL_BAR_COLOR = 'rgba(239, 239, 239, 0.15)';
const FALLBACK_SEEK_MAX = 100;
const MAX_PERCENT = 100;

/** Displays seek slider and elapsed/remaining time labels. */
export function ProgressCluster({
  width,
  largeDesktopWidth,
  currentTime,
  duration,
  remaining,
  progressColor,
  totalBarColor = DEFAULT_TOTAL_BAR_COLOR,
  handleColor,
  elapsedLabelColor,
  totalLabelColor,
  timeLabelTextStyle,
  formatTime,
  onSeek,
}: ProgressClusterProps): JSX.Element {
  const desktopBarWidth = typeof width === 'number' ? `${width}px` : width;
  const resolvedLargeDesktopBarWidth =
    largeDesktopWidth === undefined
      ? desktopBarWidth
      : typeof largeDesktopWidth === 'number'
      ? `${largeDesktopWidth}px`
      : largeDesktopWidth;
  const hasValidDuration = Number.isFinite(duration) && duration > 0;
  const seekMax = hasValidDuration ? duration : FALLBACK_SEEK_MAX;
  const progressPercent = hasValidDuration
    ? clamp((currentTime / duration) * MAX_PERCENT, 0, MAX_PERCENT)
    : 0;

  return (
    <div className="flex shrink-0 items-center gap-2 tabular-nums">
      <span
        className="flex h-5 w-[60px] shrink-0 items-center justify-end text-right"
        style={{ ...timeLabelTextStyle, color: elapsedLabelColor }}
      >
        {formatTime(currentTime)}
      </span>

      <div
        className="group/scrub relative flex h-5 w-[var(--mini-progress-bar-width-desktop)] shrink-0 items-center large-desktop:w-[var(--mini-progress-bar-width-large-desktop)]"
        style={
          {
            '--mini-progress-bar-width-desktop': desktopBarWidth,
            '--mini-progress-bar-width-large-desktop':
              resolvedLargeDesktopBarWidth,
          } as React.CSSProperties
        }
      >
        <div
          data-testid="progress-cluster-track"
          className="pointer-events-none absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
          style={{ backgroundColor: totalBarColor }}
        />
        <div
          data-testid="progress-cluster-fill"
          className="pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
          style={{
            width: `${progressPercent}%`,
            backgroundColor: progressColor,
          }}
        />
        <div
          data-testid="progress-cluster-handle"
          className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 transition-opacity duration-150 group-hover/scrub:opacity-100"
          style={{
            left: `${progressPercent}%`,
            backgroundColor: handleColor ?? progressColor,
          }}
        />
        <input
          type="range"
          min={0}
          max={seekMax}
          step={0.5}
          value={currentTime}
          onChange={onSeek}
          className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none opacity-0"
          aria-label="Seek"
        />
      </div>

      <span
        className="flex h-5 w-[60px] shrink-0 items-center"
        style={{ ...timeLabelTextStyle, color: totalLabelColor }}
      >
        -{formatTime(remaining)}
      </span>
    </div>
  );
}
